import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Companion to RouteErrorBoundary's *reactive* stale-chunk recovery (which
// only fires once a lazy import has already failed). A tab left open across
// a deploy carries the old chunk map in memory until it next crashes — this
// checks proactively, so long-lived sessions (a pinned tab, a backgrounded
// laptop) get a chance to reload on their own terms before that happens,
// rather than mid-click on whatever they're doing.
//
// Cache-busted with a query param rather than relying on Cache-Control,
// since Firebase's CDN — not just the browser — may otherwise serve a
// same-URL fetch straight from edge cache for up to an hour.
const CHECK_INTERVAL_MS = 20 * 60 * 1000;
const MAIN_JS_RE = /static\/js\/main\.[\w-]+\.js/;

function getCurrentMainJsPath() {
  const script = document.querySelector('script[src*="/static/js/main."]');
  const match = script?.getAttribute('src')?.match(MAIN_JS_RE);
  return match ? match[0] : null;
}

async function fetchLatestMainJsPath() {
  try {
    const res = await fetch(`/index.html?_v=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const html = await res.text();
    const match = html.match(MAIN_JS_RE);
    return match ? match[0] : null;
  } catch {
    return null;
  }
}

export default function UpdateAvailableBanner() {
  const { t } = useTranslation('common');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const dismissedRef = useRef(false);
  const currentRef = useRef(null);

  useEffect(() => {
    currentRef.current = getCurrentMainJsPath();
    if (!currentRef.current) return undefined;

    const check = async () => {
      if (dismissedRef.current || document.visibilityState === 'hidden') return;
      const latest = await fetchLatestMainJsPath();
      if (latest && latest !== currentRef.current) setUpdateAvailable(true);
    };

    const interval = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, []);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        background: '#111827', color: '#fff', fontSize: 13, padding: '10px 16px',
      }}
    >
      <span>{t('components.updateBanner.message', 'A new version of the app is available.')}</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: '5px 14px', borderRadius: 6, border: 'none',
          background: '#10b981', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}
      >
        {t('components.updateBanner.reload', 'Reload now')}
      </button>
      <button
        type="button"
        onClick={() => { dismissedRef.current = true; setUpdateAvailable(false); }}
        aria-label={t('components.updateBanner.dismiss', 'Dismiss')}
        style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
          fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: 4,
        }}
      >
        ×
      </button>
    </div>
  );
}
